import { useState } from 'react'
import { useCrosswordStore } from '../store'
import type { ConstructorGrid, GridPlacement } from '../types'
import { getLettersFromText } from '../lib/pairings'

type PrintMode = 'puzzle' | 'white-grid' | 'grid-only' | 'answers-landscape' | 'clues-only'
type GridInk = 'black' | 'white'

type NumberedClue = {
  number: number
  placement: GridPlacement
}

const PRINT_MODES: Array<{ id: PrintMode; label: string }> = [
  { id: 'puzzle', label: 'Puzzle' },
  { id: 'white-grid', label: 'White grid' },
  { id: 'grid-only', label: 'Grid only' },
  { id: 'answers-landscape', label: 'Answer grids' },
  { id: 'clues-only', label: 'Clues only' },
]

function getVisibleNumberCell(grid: ConstructorGrid, placement: GridPlacement): { row: number; col: number } | null {
  const length = placement.wordAText.replace(/\s/g, '').length
  for (let i = 0; i < length; i++) {
    const row = placement.direction === 'down' ? placement.row + i : placement.row
    const col = placement.direction === 'across' ? placement.col + i : placement.col
    if (row >= 0 && row < grid.height && col >= 0 && col < grid.width) {
      return { row, col }
    }
  }
  return null
}

function computeClueNumbering(grid: ConstructorGrid) {
  const acrossStarts = new Map<string, GridPlacement>()
  const downStarts = new Map<string, GridPlacement>()

  for (const p of grid.placements) {
    const numberCell = getVisibleNumberCell(grid, p)
    if (!numberCell) continue

    const key = `${numberCell.row},${numberCell.col}`
    const starts = p.direction === 'across' ? acrossStarts : downStarts
    if (!starts.has(key)) starts.set(key, p)
  }

  type StartCell = { row: number; col: number; key: string }
  const allStarts: StartCell[] = []
  for (let row = 0; row < grid.height; row++) {
    for (let col = 0; col < grid.width; col++) {
      const key = `${row},${col}`
      if (acrossStarts.has(key) || downStarts.has(key)) {
        allStarts.push({ row, col, key })
      }
    }
  }

  const priorityCells: (StartCell & { priorityValue: number })[] = []
  const normalCells: StartCell[] = []

  for (const cell of allStarts) {
    const across = acrossStarts.get(cell.key)
    const down = downStarts.get(cell.key)
    const acrossPri = across?.priority
    const downPri = down?.priority

    if (acrossPri != null || downPri != null) {
      const value = Math.min(acrossPri ?? Infinity, downPri ?? Infinity)
      priorityCells.push({ ...cell, priorityValue: value })
    } else {
      normalCells.push(cell)
    }
  }

  priorityCells.sort((a, b) =>
    a.priorityValue - b.priorityValue || a.row - b.row || a.col - b.col
  )

  const cellNumbers = new Map<string, number>()
  const acrossClues: NumberedClue[] = []
  const downClues: NumberedClue[] = []
  let nextNumber = 1

  const assignNumber = (cell: StartCell) => {
    const number = nextNumber++
    cellNumbers.set(cell.key, number)

    const acrossPlacement = acrossStarts.get(cell.key)
    const downPlacement = downStarts.get(cell.key)
    if (acrossPlacement) acrossClues.push({ number, placement: acrossPlacement })
    if (downPlacement) downClues.push({ number, placement: downPlacement })
  }

  for (const cell of priorityCells) assignNumber(cell)
  for (const cell of normalCells) assignNumber(cell)

  return { cellNumbers, acrossClues, downClues }
}

function buildCellLetterMap(grid: ConstructorGrid) {
  const map = new Map<string, { a: string; b: string }>()
  for (const p of grid.placements) {
    const lettersA = getLettersFromText(p.wordAText)
    const lettersB = getLettersFromText(p.wordBText)
    for (let i = 0; i < lettersA.length; i++) {
      const r = p.direction === 'down' ? p.row + i : p.row
      const c = p.direction === 'across' ? p.col + i : p.col
      const key = `${r},${c}`
      if (!map.has(key)) {
        map.set(key, { a: lettersA[i] || '', b: lettersB[i] || '' })
      }
    }
  }
  return map
}

function PuzzleGrid({
  grid,
  cellNumbers,
  cellSize,
  ink,
  cellLetters,
  answerSide,
}: {
  grid: ConstructorGrid
  cellNumbers: Map<string, number>
  cellSize: number
  ink: GridInk
  cellLetters?: Map<string, { a: string; b: string }> | undefined
  answerSide?: 'a' | 'b' | undefined
}) {
  const gridWidthPx = grid.width * cellSize
  const gridHeightPx = grid.height * cellSize
  const isWhiteGrid = ink === 'white'

  return (
    <svg
      viewBox={`0 0 ${gridWidthPx} ${gridHeightPx}`}
      width={gridWidthPx}
      height={gridHeightPx}
      className="print-grid"
    >
      {Array.from({ length: grid.height }, (_, row) =>
        Array.from({ length: grid.width }, (_, col) => {
          const cell = grid.cells[row]?.[col]
          const hasContent = cell?.placementId != null
          const isFillable = hasContent && !cell?.isBlocked
          const key = `${row},${col}`
          const number = cellNumbers.get(key)
          const letters = cellLetters?.get(key)
          const letter = answerSide && letters ? letters[answerSide] : undefined
          const showLetter = letter && letter !== '□' && isFillable
          const x = col * cellSize
          const y = row * cellSize
          const fill = !isWhiteGrid && !isFillable ? '#1a1a1a' : '#ffffff'
          const stroke = !isWhiteGrid && !isFillable ? '#1a1a1a' : isFillable ? '#888888' : '#d1d5db'
          const strokeWidth = !isWhiteGrid && !isFillable ? 0 : 0.75

          return (
            <g key={key}>
              <rect
                x={x}
                y={y}
                width={cellSize}
                height={cellSize}
                fill={fill}
                stroke={stroke}
                strokeWidth={strokeWidth}
              />
              {number && isFillable && (
                <text
                  x={x + 1.5}
                  y={y + (cellSize < 24 ? 7 : 9.5)}
                  fontSize={cellSize < 24 ? 6.5 : 8}
                  fontFamily="DM Sans, sans-serif"
                  fontWeight="600"
                  fill="#000000"
                >
                  {number}
                </text>
              )}
              {showLetter && (
                <text
                  x={x + cellSize / 2}
                  y={y + cellSize * 0.72}
                  fontSize={cellSize * 0.48}
                  fontFamily="DM Sans, sans-serif"
                  fontWeight="500"
                  fill="#111827"
                  textAnchor="middle"
                >
                  {letter}
                </text>
              )}
            </g>
          )
        })
      )}
      <rect
        x={0}
        y={0}
        width={gridWidthPx}
        height={gridHeightPx}
        fill="none"
        stroke="#000000"
        strokeWidth={2}
      />
    </svg>
  )
}

function SolvedGrid({ grid, cellNumbers, cellLetters, side, label, cellSize }: {
  grid: ConstructorGrid
  cellNumbers: Map<string, number>
  cellLetters: Map<string, { a: string; b: string }>
  side: 'a' | 'b'
  label: string
  cellSize: number
}) {
  return (
    <div className="print-solved-grid">
      <div className="print-solved-grid-label">{label}</div>
      <PuzzleGrid
        grid={grid}
        cellNumbers={cellNumbers}
        cellSize={cellSize}
        ink="white"
        cellLetters={cellLetters}
        answerSide={side}
      />
    </div>
  )
}

function ClueColumns({ acrossClues, downClues, renderClue }: {
  acrossClues: NumberedClue[]
  downClues: NumberedClue[]
  renderClue: (clue: NumberedClue) => React.ReactNode
}) {
  return (
    <div className="print-clues">
      <div className="print-clues-columns">
        <div className="print-clue-section">
          <h3 className="print-clue-heading">Across</h3>
          {acrossClues.map(renderClue)}
        </div>
        <div className="print-clue-section">
          <h3 className="print-clue-heading">Down</h3>
          {downClues.map(renderClue)}
        </div>
      </div>
    </div>
  )
}

export function PrintView() {
  const grid = useCrosswordStore(s => s.constructorGrid)
  const pairings = useCrosswordStore(s => s.pairings)
  const listNames = useCrosswordStore(s => s.listNames)
  const [mode, setMode] = useState<PrintMode>('puzzle')

  if (!grid) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        No grid to print. Create a grid in the Constructor first.
      </div>
    )
  }

  const { cellNumbers, acrossClues, downClues } = computeClueNumbering(grid)
  const cellLetters = buildCellLetterMap(grid)

  const getClueText = (placement: GridPlacement): string => {
    if (placement.pairingId) {
      const pairing = pairings.find(p => p.id === placement.pairingId)
      if (pairing?.clue?.text) return pairing.clue.text
    }
    return ''
  }

  const renderClue = ({ number, placement }: NumberedClue) => {
    const wordLength = placement.wordAText.replace(/\s/g, '').length
    const clueText = getClueText(placement)
    const hasClue = clueText.length > 0

    return (
      <div key={`${number}-${placement.id}`} className="print-clue">
        <span className="print-clue-number">{number}</span>
        <span className="print-clue-text">
          {hasClue ? clueText : '___'} <span className="print-clue-length">({wordLength})</span>
        </span>
      </div>
    )
  }

  const maxDimension = Math.max(grid.width, grid.height)
  const standardCellSize = Math.min(Math.floor(500 / maxDimension), 36)
  const fullGridCellSize = Math.min(Math.floor(720 / maxDimension), 48)
  const answerCellSize = Math.min(Math.floor(940 / 2 / maxDimension), 32)

  const renderGridAndClues = (ink: GridInk) => (
    <div className="print-page">
      <div
        className="print-grid-container"
        style={{ width: grid.width * standardCellSize, height: grid.height * standardCellSize }}
      >
        <PuzzleGrid
          grid={grid}
          cellNumbers={cellNumbers}
          cellSize={standardCellSize}
          ink={ink}
        />
      </div>
      <ClueColumns acrossClues={acrossClues} downClues={downClues} renderClue={renderClue} />
      <div className="print-legend">
        <p className="print-legend-text">
          {listNames.A} / {listNames.B}
        </p>
      </div>
    </div>
  )

  const page =
    mode === 'puzzle' ? renderGridAndClues('black')
      : mode === 'white-grid' ? renderGridAndClues('white')
        : mode === 'grid-only' ? (
          <div className="print-page print-page-grid-only">
            <div
              className="print-grid-container print-grid-container-full"
              style={{ width: grid.width * fullGridCellSize, height: grid.height * fullGridCellSize }}
            >
              <PuzzleGrid
                grid={grid}
                cellNumbers={cellNumbers}
                cellSize={fullGridCellSize}
                ink="black"
              />
            </div>
          </div>
        )
          : mode === 'answers-landscape' ? (
            <div className="print-page print-page-landscape">
              <div className="print-solved-grid-pair">
                <SolvedGrid
                  grid={grid}
                  cellNumbers={cellNumbers}
                  cellLetters={cellLetters}
                  side="a"
                  label={listNames.A}
                  cellSize={answerCellSize}
                />
                <SolvedGrid
                  grid={grid}
                  cellNumbers={cellNumbers}
                  cellLetters={cellLetters}
                  side="b"
                  label={listNames.B}
                  cellSize={answerCellSize}
                />
              </div>
            </div>
          )
            : (
              <div className="print-page print-page-clues-only">
                <ClueColumns acrossClues={acrossClues} downClues={downClues} renderClue={renderClue} />
                <div className="print-legend">
                  <p className="print-legend-text">
                    {listNames.A} / {listNames.B}
                  </p>
                </div>
              </div>
            )

  return (
    <div className="print-view" data-print-mode={mode}>
      <div className="print-actions no-print">
        <ModeToggle mode={mode} onChange={setMode} />
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
        >
          Print / Save PDF
        </button>
      </div>

      {page}
    </div>
  )
}

function ModeToggle({ mode, onChange }: { mode: PrintMode; onChange: (m: PrintMode) => void }) {
  return (
    <div className="print-mode-toolbar">
      {PRINT_MODES.map(printMode => (
        <button
          key={printMode.id}
          onClick={() => onChange(printMode.id)}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            mode === printMode.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          {printMode.label}
        </button>
      ))}
    </div>
  )
}
